/**
 * @fileoverview Event Bus Service — single multiplexed SSE per dashboard.
 *
 * Connects to `GET /events/stream` (optionally with scope params) and
 * dispatches events to subscribed handlers by event-type string.
 *
 * Replaces the per-feature SSE services (notification-events,
 * session-events, company-events, staff-events, support-ticket-events)
 * with one connection per session, eliminating HTTP/1.1 connection-pool
 * starvation when admin + store dashboards run against the same backend.
 *
 * Public surface (use via `useEventBus()`):
 *   - `connect(scope)`           — open / replace the stream
 *   - `disconnect()`             — close + clear state
 *   - `subscribe(type, handler)` — returns an unsubscribe function
 *
 * Behaviour:
 *   - Exponential backoff 1s → 30s, infinite retries.
 *   - Reconnects when the tab becomes visible after a drop.
 *   - Watchdog anti-zombie: sin frames (ni heartbeat) en 75s → reconecta.
 *   - 401 / 403 stops reconnection (auth flow takes over).
 *   - 429 respects `Retry-After` (consistent with the other admin SSE
 *     services in this codebase).
 *   - Server-managed events (`heartbeat`, `connected`) consumed silently.
 */

import { tokenService } from './token.service';
import { authService, RefreshAuthError } from './auth.service';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

/**
 * Watchdog anti-zombie: el server manda un heartbeat cada 25s. Si no llega
 * NINGÚN frame en este lapso (3 pings perdidos), la conexión está muerta
 * aunque `fetch` no lo reporte — suspensión del equipo, cambio de red o un
 * NAT timeout dejan a `reader.read()` colgado para siempre con estado
 * 'connected', y el dashboard deja de recibir eventos sin enterarse.
 */
const STALE_CONNECTION_MS = 75_000;
const WATCHDOG_INTERVAL_MS = 15_000;

export type EventScope =
  | { type: 'store'; id: string }
  | { type: 'company'; id: string }
  | { type: 'platform'; id: string };

export type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'denied';

type EventHandler = (data: unknown) => void;

interface StateChangeListener {
  (state: ConnectionState): void;
}

interface ReconnectListener {
  (): void;
}

export class EventBusServiceImpl {
  private abortController: AbortController | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private currentScope: EventScope | null = null;

  private subscriptions = new Map<string, Set<EventHandler>>();
  private stateListeners = new Set<StateChangeListener>();
  private reconnectListeners = new Set<ReconnectListener>();

  private state: ConnectionState = 'idle';
  private hasConnectedOnce = false;
  private visibilityHandler: (() => void) | null = null;

  /** Timestamp del último frame recibido (heartbeat incluido). */
  private lastFrameAt = 0;

  /** Timer del watchdog anti-zombie. */
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;

  /** Flag para evitar loop infinito refresh→401→refresh. Se resetea
   *  al conectar exitosamente o al cambiar de scope. */
  private retriedAfter401 = false;

  // -----------------------------------------
  // PUBLIC API
  // -----------------------------------------

  connect(scope: EventScope | null): void {
    if (
      this.state === 'connected' &&
      this.scopesEqual(this.currentScope, scope)
    ) {
      return;
    }
    this.cleanup();
    this.currentScope = scope;
    this.retriedAfter401 = false;
    void this.startConnection();

    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.visibilityState !== 'visible') return;
        if (this.state === 'connected') {
          // Los timers en background se throttlean — al volver a foco,
          // validar de inmediato que la conexión no quedó zombie.
          this.reconnectIfStale();
          return;
        }
        if (this.state === 'connecting') return;
        // Cuando volvemos a foco, reintentamos incluso desde `denied`:
        // un 401/403 puede haberse causado por un token expirado que el
        // usuario refrescó en otra tab. Si el problema persiste, el
        // próximo response 401 nos va a llevar de vuelta a `denied`.
        if (this.state === 'denied') {
          this.setState('idle');
        }
        this.reconnectAttempts = 0;
        this.retriedAfter401 = false;
        void this.startConnection();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    if (!this.watchdogTimer) {
      this.watchdogTimer = setInterval(
        () => this.reconnectIfStale(),
        WATCHDOG_INTERVAL_MS,
      );
    }
  }

  /**
   * Close the stream and clear ALL state — incluyendo el registro de
   * subscriptions. Pensado para ejecutarse cuando el usuario hace
   * logout: cualquier handler residual queda con cierres apuntando
   * a state stale (componentes desmontados, profile distinto si
   * hay re-login con otro user). Limpiar es la opción segura.
   */
  disconnect(): void {
    this.cleanup();
    this.currentScope = null;
    this.hasConnectedOnce = false;
    this.subscriptions.clear();
  }

  subscribe(eventType: string, handler: EventHandler): () => void {
    let handlers = this.subscriptions.get(eventType);
    if (!handlers) {
      handlers = new Set();
      this.subscriptions.set(eventType, handlers);
    }
    handlers.add(handler);
    return () => {
      const set = this.subscriptions.get(eventType);
      if (!set) return;
      set.delete(handler);
      if (set.size === 0) this.subscriptions.delete(eventType);
    };
  }

  onStateChange(listener: StateChangeListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => this.stateListeners.delete(listener);
  }

  onReconnect(listener: ReconnectListener): () => void {
    this.reconnectListeners.add(listener);
    return () => this.reconnectListeners.delete(listener);
  }

  getState(): ConnectionState {
    return this.state;
  }

  // -----------------------------------------
  // PRIVATE — CONNECTION
  // -----------------------------------------

  private async startConnection(): Promise<void> {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    if (!tokenService.hasToken()) {
      this.scheduleReconnect();
      return;
    }

    // Refresh proactivo: si el access token ya venció (o está por
    // vencer), pedimos refresh antes de abrir el stream. Sin esto, el
    // backend nos contesta 401 "No token provided", pasamos a `denied`
    // y nos quedamos sin SSE hasta el próximo visibilitychange.
    if (tokenService.isTokenExpired()) {
      try {
        await authService.refreshAccessToken();
      } catch (err) {
        if (err instanceof RefreshAuthError) {
          this.setState('denied');
          return;
        }
        // Network/timeout → reintento con backoff (no es problema de auth).
        this.scheduleReconnect();
        return;
      }
      if (signal.aborted) return;
    }

    this.setState(this.hasConnectedOnce ? 'reconnecting' : 'connecting');

    const deviceId = tokenService.getExistingDeviceId();
    const url = this.buildUrl();

    try {
      const response = await fetch(url, {
        headers: {
          ...(deviceId ? { 'x-device-id': deviceId } : {}),
          'x-platform': 'DASHBOARD_ADMIN',
        },
        credentials: 'include',
        signal,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // 401 puede ser auth expirada justo entre el check y el fetch
          // (race con otro tab que invalidó el token). Intentamos un
          // refresh + reintento UNA vez antes de rendirnos a `denied`.
          // Sin esto, el stream queda colgado hasta que el tab pierda
          // y recupere foco.
          if (response.status === 401 && !this.retriedAfter401) {
            this.retriedAfter401 = true;
            try {
              await authService.refreshAccessToken();
              if (signal.aborted) return;
              void this.startConnection();
              return;
            } catch {
              // Cae al `denied` de abajo
            }
          }
          this.setState('denied');
          return;
        }
        if (response.status === 429) {
          const retryAfter = Number(response.headers.get('retry-after'));
          const delayMs =
            Number.isFinite(retryAfter) && retryAfter > 0
              ? retryAfter * 1000
              : 10_000;
          this.scheduleReconnect(delayMs);
          return;
        }
        throw new Error(`SSE failed: HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No readable stream');

      const wasReconnect = this.hasConnectedOnce;
      this.hasConnectedOnce = true;
      this.reconnectAttempts = 0;
      this.retriedAfter401 = false;
      this.lastFrameAt = Date.now();
      this.setState('connected');

      if (wasReconnect) {
        for (const cb of this.reconnectListeners) {
          try {
            cb();
          } catch (e) {
            console.error('[EventBus] reconnect listener error', e);
          }
        }
      }

      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        // Cualquier byte del server (heartbeat incluido) cuenta como vida.
        this.lastFrameAt = Date.now();
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          if (frame.trim()) this.dispatchFrame(frame);
        }
      }

      this.setState('reconnecting');
      this.scheduleReconnect();
    } catch (err) {
      if (signal.aborted) return;
      void err;
      this.setState('reconnecting');
      this.scheduleReconnect();
    }
  }

  private dispatchFrame(raw: string): void {
    const lines = raw.split('\n');
    let eventType = '';
    let dataStr = '';
    for (const line of lines) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim();
      else if (line.startsWith('data:')) {
        dataStr += (dataStr ? '\n' : '') + line.slice(5).trim();
      }
    }
    if (!eventType) return;
    if (eventType === 'heartbeat' || eventType === 'connected') return;

    let parsed: unknown;
    if (dataStr) {
      try {
        parsed = JSON.parse(dataStr);
      } catch {
        return;
      }
    } else {
      parsed = null;
    }

    const handlers = this.subscriptions.get(eventType);
    if (!handlers || handlers.size === 0) return;
    for (const handler of handlers) {
      try {
        handler(parsed);
      } catch (e) {
        console.error(`[EventBus] handler error for "${eventType}"`, e);
      }
    }
  }

  // -----------------------------------------
  // PRIVATE — RECONNECT
  // -----------------------------------------

  /**
   * Mata conexiones zombie: estado 'connected' sin frames (ni heartbeats)
   * en STALE_CONNECTION_MS. Reabre el stream; al reconectar disparan los
   * reconnect listeners y las páginas hacen su refetch de catch-up.
   */
  private reconnectIfStale(): void {
    if (this.state !== 'connected') return;
    if (Date.now() - this.lastFrameAt < STALE_CONNECTION_MS) return;
    console.warn(
      '[EventBus] conexión sin frames en 75s — reconectando (zombie)',
    );
    this.reconnectAttempts = 0;
    void this.startConnection();
  }

  private scheduleReconnect(forceDelayMs?: number): void {
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    const delay =
      forceDelayMs ?? Math.min(1000 * 2 ** this.reconnectAttempts, 30_000);
    this.reconnectAttempts++;
    this.reconnectTimeout = setTimeout(() => {
      void this.startConnection();
    }, delay);
  }

  // -----------------------------------------
  // PRIVATE — HELPERS
  // -----------------------------------------

  private buildUrl(): string {
    const scope = this.currentScope;
    if (!scope) return `${API_BASE_URL}/events/stream`;
    const params = new URLSearchParams({ scope: scope.type, id: scope.id });
    return `${API_BASE_URL}/events/stream?${params.toString()}`;
  }

  private scopesEqual(a: EventScope | null, b: EventScope | null): boolean {
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;
    return a.type === b.type && a.id === b.id;
  }

  private cleanup(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.setState('idle');
  }

  private setState(next: ConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    for (const listener of this.stateListeners) {
      try {
        listener(next);
      } catch (e) {
        console.error('[EventBus] state listener error', e);
      }
    }
  }
}

export const eventBusService = new EventBusServiceImpl();
